import {
  getCoverEventPosterImage,
} from '@/features/events/shared/FocalEventPosterImage';
import { RsvpConfirmSheet } from '@/features/events/shared/RsvpConfirmSheet';
import {
  WebFeedRail,
  type WebFeedRailEvent,
  type WebFeedRailScene,
} from '@/features/events/feed/components/WebFeedRail';
import { WebEventCard } from '@/features/events/feed/components/WebEventCard';
import { WebFeedDealCard } from '@/features/events/feed/components/WebFeedDealCard';
import { WebDealStrip } from '@/features/events/feed/components/WebDealStrip';
import { getAbsoluteWebFeedHref } from '@/features/events/feed/webFeedLinks';
import { isWebFeedEventHappeningNow } from '@/features/events/feed/webFeedTiming';
import { Button } from '@/components/ui/button';
import { Icon } from '@/components/ui/icon';
import { Text } from '@/components/ui/text';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  DISTANCE_FILTERS,
  ENTRY_TYPE_FILTERS,
  TIME_FILTERS,
  getDistanceFilterLabel,
  getTimeFilterLabel,
  type EntryTypeFilter,
} from '@/features/events/feed/eventsFeedShared';
import { buildDateGroupedFeedRows } from '@/features/events/feed/dateGroupedFeed';
import {
  DealTimingStateArchived,
  EntryTypeFreeRSVP,
  EntryTypeRSVP,
  FeedItemTypeDeal,
  FeedItemTypeEvent,
  EntryTypeTicketed,
  EventMomentumHot,
  EventMomentumTrending,
  RSVPVisibilityPublic,
  type FeedDealItem,
  type FeedEvent,
  type FeedItem,
  type RSVPVisibility,
} from '@/lib/api/apiTypes';
import { useFeedPreferences } from '@/features/events/feed/feedPreferences';
import type { FeedDistanceFilter, FeedTimeWindow } from '@/features/events/feed/feedPreferences';
import {
  DISCOVER_RADIUS_METERS,
  flattenNearbyFeedItems,
  type FeedMode,
} from '@/features/events/feed/feedQuery';
import { getEventTagLabel, type EventTagGroup } from '@/features/events/eventTags';
import { useEventTagCatalog } from '@/features/events/useEventTagCatalog';
import { ManageRsvpSheetContent } from '@/features/events/eventDetail/EventDetailSections';
import {
  loadRSVPVisibilityPreference,
  saveRSVPVisibilityPreference,
} from '@/features/events/rsvpVisibilityPreference';
import { useAuth } from '@/lib/auth/auth';
import { openDirections } from '@/lib/location/directions';
import {
  getEventRSVPButtonLabel,
  isTicketedEventWithURL,
  normalizeExternalHTTPURL,
} from '@/features/events/eventMobileHelpers';
import { useEventFeedMutations } from '@/features/events/feed/useEventFeedMutations';
import { useFeedDealMutations } from '@/features/events/feed/useFeedDealMutations';
import {
  trackDealDirectionsTapped,
  trackDealImpression,
  trackDealSaved,
  trackDealShare,
  trackEventFeedImpression,
  trackFeedFilterApplied,
  trackTicketLinkTapped,
} from '@/lib/analytics/posthog';
import { formatEventDayLabel, formatEventTimeRange } from '@/features/events/eventFormatting';
import { getEventHref, getVenueHref } from '@/lib/navigation/routes';
import { shareLink } from '@/lib/platform/share';
import { useNearbyFeed } from '@/features/events/feed/useNearbyFeed';
import { formatWebFeedDistanceLabel } from '@/features/events/feed/feedFormatters';
import { showToast } from '@/lib/useToastStore';
import { cn } from '@/lib/utils';
import { router } from 'expo-router';
import * as WebBrowser from 'expo-web-browser';
import {
  CalendarDaysIcon,
  ChevronDownIcon,
  SearchIcon,
} from 'lucide-react-native';
import * as React from 'react';
import { Pressable, ScrollView, useWindowDimensions, View } from 'react-native';

type WebFeedMode = 'discover' | 'nearby';
const WEB_DISCOVER_FALLBACK_WINDOWS: FeedTimeWindow[] = ['week', 'all'];
const WEB_DISCOVER_MIN_RESULTS = 3;

type WebDiscoverFallback = {
  entryTypeFilter: EntryTypeFilter;
  sceneTagFilter: string;
  stage: 'broaden' | 'relax';
  timeWindow: FeedTimeWindow;
};

export default function EventsWebScreen() {
  const { width } = useWindowDimensions();
  const isCompactWeb = width < 640;
  const isRailVisible = width >= 1024;
  const session = useAuth((state) => state.session);
  const feedTab = useFeedPreferences((state) => state.feedTab);
  const setFeedTab = useFeedPreferences((state) => state.setFeedTab);
  const timeWindow = useFeedPreferences((state) => state.timeWindow);
  const setTimeWindow = useFeedPreferences((state) => state.setTimeWindow);
  const radiusMeters = useFeedPreferences((state) => state.radiusMeters);
  const setRadiusMeters = useFeedPreferences((state) => state.setRadiusMeters);
  const [entryTypeFilter, setEntryTypeFilter] = React.useState<EntryTypeFilter>('all');
  const [sceneTagFilter, setSceneTagFilter] = React.useState('all');
  const [actionNotice, setActionNotice] = React.useState<string | null>(null);
  const [rsvpDialogEvent, setRsvpDialogEvent] = React.useState<FeedEvent | null>(null);
  const [rsvpVisibility, setRsvpVisibility] =
    React.useState<RSVPVisibility>(RSVPVisibilityPublic);
  const [discoverFallback, setDiscoverFallback] = React.useState<WebDiscoverFallback | null>(null);
  const tagCatalogQuery = useEventTagCatalog();
  const eventMutations = useEventFeedMutations('nearby', { refetchOnRsvpSettled: false });
  const dealMutations = useFeedDealMutations();
  const trackedImpressionsRef = React.useRef(new Set<string>());
  const analyticsSessionRef = React.useRef(`web-events-${Date.now()}`);

  const requestSignIn = React.useCallback((action: string) => {
    setActionNotice(`Sign in to ${action} and keep it synced in Radious.`);
  }, []);

  const closeRsvpDialog = React.useCallback(() => {
    setRsvpDialogEvent(null);
  }, []);

  const handleRsvpVisibilityChange = React.useCallback((visibility: RSVPVisibility) => {
    setRsvpVisibility(visibility);
    saveRSVPVisibilityPreference(visibility).catch(() => undefined);
  }, []);

  const activeSummary = React.useMemo(() => {
    return [
      feedTab === 'discover' ? 'Smart discovery' : 'Nearby first',
      getTimeFilterLabel(timeWindow),
      getDistanceFilterLabel(radiusMeters),
      getEntryFilterLabel(entryTypeFilter),
      sceneTagFilter === 'all' ? 'All scenes' : getEventTagLabel(sceneTagFilter),
    ];
  }, [entryTypeFilter, feedTab, radiusMeters, sceneTagFilter, timeWindow]);

  const handleFeedTabChange = React.useCallback(
    (value: typeof feedTab) => {
      setDiscoverFallback(null);
      setFeedTab(value);
      trackFeedFilterApplied('mode', value);
    },
    [setFeedTab]
  );

  const handleTimeWindowChange = React.useCallback(
    (value: typeof timeWindow) => {
      setDiscoverFallback(null);
      setTimeWindow(value);
      trackFeedFilterApplied('time_window', value);
    },
    [setTimeWindow]
  );

  const handleRadiusChange = React.useCallback(
    (value: FeedDistanceFilter) => {
      setRadiusMeters(value);
      trackFeedFilterApplied('radius', String(value));
    },
    [setRadiusMeters]
  );

  const handleEntryTypeChange = React.useCallback((value: string) => {
    setDiscoverFallback(null);
    const next = value as EntryTypeFilter;
    setEntryTypeFilter(next);
  }, []);

  const handleSceneTagChange = React.useCallback((value: string) => {
    setDiscoverFallback(null);
    setSceneTagFilter(value);
  }, []);

  const feedMode: FeedMode = feedTab === 'discover' ? 'smart' : 'proximity';
  const feedRadiusMeters = feedTab === 'discover' ? DISCOVER_RADIUS_METERS : radiusMeters;
  const effectiveTimeWindow =
    feedTab === 'discover' ? discoverFallback?.timeWindow ?? timeWindow : timeWindow;
  const effectiveEntryTypeFilter =
    feedTab === 'discover' ? discoverFallback?.entryTypeFilter ?? entryTypeFilter : entryTypeFilter;
  const effectiveSceneTagFilter =
    feedTab === 'discover' ? discoverFallback?.sceneTagFilter ?? sceneTagFilter : sceneTagFilter;
  const nearbyFeedQuery = useNearbyFeed({
    mode: feedMode,
    radiusMeters: feedRadiusMeters,
    timeWindow: effectiveTimeWindow,
  });
  const feedItems = React.useMemo(
    () => flattenNearbyFeedItems(nearbyFeedQuery.data?.pages),
    [nearbyFeedQuery.data?.pages]
  );
  const baseFeedEvents = React.useMemo(
    () =>
      feedItems.flatMap((item) => (item.event ? [item.event] : [])),
    [feedItems]
  );
  const sceneOptions = React.useMemo(
    () => buildSceneFilterGroups(baseFeedEvents, tagCatalogQuery.data),
    [baseFeedEvents, tagCatalogQuery.data]
  );
  const visibleFeedItems = React.useMemo(() => {
    return filterWebFeedItemsByControls(feedItems, effectiveEntryTypeFilter, effectiveSceneTagFilter);
  }, [effectiveEntryTypeFilter, effectiveSceneTagFilter, feedItems]);

  const visibleFeedEvents = React.useMemo(
    () =>
      visibleFeedItems.flatMap((item) => (item.event ? [item.event] : [])),
    [visibleFeedItems]
  );
  const railData = React.useMemo(() => buildWebFeedRailData(visibleFeedEvents), [visibleFeedEvents]);

  React.useEffect(() => {
    const contextKey = activeSummary.join('|');
    visibleFeedItems.forEach((item, index) => {
      if (item.type === FeedItemTypeEvent && item.event) {
        const key = `event:${item.event.id}:${contextKey}`;
        if (trackedImpressionsRef.current.has(key)) return;
        trackedImpressionsRef.current.add(key);
        trackEventFeedImpression({
          eventID: item.event.id,
          position: index,
          sessionKey: analyticsSessionRef.current,
          contextKey,
          surface: 'web',
          venueID: item.event.venue_id,
        });
        return;
      }
      if (item.type === FeedItemTypeDeal && item.deal) {
        const key = `deal:${item.deal.deal.id}:${contextKey}`;
        if (trackedImpressionsRef.current.has(key)) return;
        trackedImpressionsRef.current.add(key);
        trackDealImpression({
          category: item.deal.deal.category,
          dealID: item.deal.deal.id,
          position: index,
          source: 'feed',
          surface: 'web',
          timingState: item.deal.timing_state,
          venueID: item.deal.deal.venue_id,
        });
      }
    });
  }, [activeSummary, visibleFeedItems]);

  React.useEffect(() => {
    if (
      feedTab !== 'discover' ||
      nearbyFeedQuery.isPending ||
      nearbyFeedQuery.isFetching ||
      nearbyFeedQuery.isError ||
      (discoverFallback
        ? visibleFeedItems.length > 0
        : timeWindow !== 'today' || visibleFeedItems.length >= WEB_DISCOVER_MIN_RESULTS)
    ) {
      return;
    }

    const nextFallback = getNextWebDiscoverFallback({
      currentFallback: discoverFallback,
      entryTypeFilter,
      sceneTagFilter,
      timeWindow,
    });
    if (!nextFallback) {
      return;
    }

    setDiscoverFallback(nextFallback);
  }, [
    discoverFallback,
    entryTypeFilter,
    feedTab,
    nearbyFeedQuery.isError,
    nearbyFeedQuery.isFetching,
    nearbyFeedQuery.isPending,
    sceneTagFilter,
    timeWindow,
    visibleFeedItems.length,
  ]);

  React.useEffect(() => {
    let cancelled = false;

    loadRSVPVisibilityPreference(true)
      .then((visibility) => {
        if (!cancelled) {
          setRsvpVisibility(visibility);
        }
      })
      .catch(() => undefined);

    return () => {
      cancelled = true;
    };
  }, [session?.me.user.id]);

  const handleEventRsvp = React.useCallback(
    async (event: FeedEvent) => {
      if (isTicketedEventWithURL(event)) {
        const ticketURL = normalizeExternalHTTPURL(event.ticket_url);
        if (!ticketURL) {
          showToast({ message: 'Could not open the ticket link. Try again in a moment.' });
          return;
        }
        await trackTicketLinkTapped({
          eventID: event.id,
          isPaid: true,
          source: 'feed',
          surface: 'web',
          venueID: event.venue_id,
        }).catch(() => undefined);
        await WebBrowser.openBrowserAsync(ticketURL).catch(() => {
          showToast({ message: 'Could not open the ticket link. Try again in a moment.' });
        });
        return;
      }
      if (event.entry_type === EntryTypeTicketed) {
        showToast({ message: 'Ticket link is not available yet.' });
        return;
      }
      if (!session) {
        requestSignIn(getEventRSVPButtonLabel(event));
        return;
      }
      setRsvpDialogEvent(event);
    },
    [requestSignIn, session]
  );

  const handleConfirmRsvpDialog = React.useCallback(async () => {
    if (!rsvpDialogEvent) return;
    try {
      await eventMutations.confirmGoing.mutateAsync({
        event: rsvpDialogEvent,
        visibility: rsvpVisibility,
        source: 'feed',
      });
      closeRsvpDialog();
    } catch {
      showToast({ message: 'Could not update your RSVP. Try again in a moment.' });
    }
  }, [closeRsvpDialog, eventMutations.confirmGoing, rsvpDialogEvent, rsvpVisibility]);

  const handleCancelRsvpDialog = React.useCallback(async () => {
    if (!rsvpDialogEvent) return;
    const eventID = rsvpDialogEvent.id;
    closeRsvpDialog();
    try {
      await eventMutations.removeRsvp.mutateAsync({ eventID });
    } catch {
      showToast({ message: 'Could not remove that RSVP. Try again in a moment.' });
    }
  }, [closeRsvpDialog, eventMutations.removeRsvp, rsvpDialogEvent]);

  const handleEventToggleSaved = React.useCallback(
    async (event: FeedEvent) => {
      if (!session) {
        requestSignIn('save events');
        return;
      }
      try {
        await eventMutations.toggleSaved.mutateAsync({ event });
      } catch {
        showToast({ message: 'Could not update that saved event. Try again in a moment.' });
      }
    },
    [eventMutations.toggleSaved, requestSignIn, session]
  );

  const handleDealSave = React.useCallback(
    async (item: FeedDealItem) => {
      if (!session) {
        requestSignIn('save deals');
        return;
      }
      try {
        await dealMutations.toggleSaved.mutateAsync({ deal: item.deal });
        trackDealSaved({
          dealID: item.deal.id,
          isSaved: !item.deal.is_saved,
          source: 'feed',
          surface: 'web',
          venueID: item.deal.venue_id,
        });
      } catch {
        showToast({ message: 'Could not update that saved deal. Try again in a moment.' });
      }
    },
    [dealMutations.toggleSaved, requestSignIn, session]
  );

  const handleDealShare = React.useCallback(async (item: FeedDealItem) => {
    const url = getAbsoluteWebFeedHref(
      getVenueHref(item.deal.venue_id, {}, { name: item.deal.venue_name })
    );
    const result = await shareLink({
      message: `${item.deal.title} at ${item.deal.venue_name} on Radious`,
      title: item.deal.title,
      url,
    });
    if (result !== 'failed') {
      trackDealShare({
        dealID: item.deal.id,
        source: 'feed',
        surface: 'web',
        venueID: item.deal.venue_id,
      });
    }
  }, []);

  const handleDealDirections = React.useCallback((item: FeedDealItem) => {
    if (typeof item.deal.latitude !== 'number' || typeof item.deal.longitude !== 'number') {
      showToast({ message: 'Directions are not available for this deal yet.' });
      return;
    }
    trackDealDirectionsTapped({
      dealID: item.deal.id,
      source: 'feed',
      surface: 'web',
      venueID: item.deal.venue_id,
    });
    openDirections(item.deal.latitude, item.deal.longitude).catch(() => undefined);
  }, []);

  return (
    <View className="h-screen bg-background">
      <ScrollView
        className="h-full"
        contentContainerClassName="pb-16"
        showsVerticalScrollIndicator={false}>
        {/* Top bar */}
        <View className="border-b border-border/50 bg-background">
          <View
            className="mx-auto w-full max-w-[1300px] gap-3.5 pb-4 pt-5"
            style={{ paddingHorizontal: isCompactWeb ? 22 : 24 }}>
            <View
              className="items-start justify-between gap-3"
              style={{ flexDirection: isCompactWeb ? 'column' : 'row' }}>
              <Text className="text-[28px] font-bold tracking-tight text-foreground">Events</Text>
              <View className="flex-row items-center gap-0.5 rounded-[10px] border border-border bg-surface p-0.5">
                <ModeTab
                  active={feedTab === 'discover'}
                  label="Discover"
                  onPress={() => handleFeedTabChange('discover')}
                />
                <ModeTab
                  active={feedTab === 'nearby'}
                  label="Nearby"
                  onPress={() => handleFeedTabChange('nearby')}
                />
              </View>
            </View>

            {isCompactWeb ? (
              <Pressable
                accessibilityRole="search"
                className="min-h-14 flex-row items-center gap-3 rounded-2xl border border-border bg-surface-alt px-3 active:opacity-80"
                onPress={() => router.push('/search')}>
                <Icon as={SearchIcon} size={16} className="text-muted-foreground" />
                <Text className="flex-1 text-[14px] text-muted-foreground" numberOfLines={1}>
                  Search events, deals, venues, or people
                </Text>
              </Pressable>
            ) : null}

            <View className="flex-row flex-wrap gap-2">
              <DropdownFilterPill
                label={getTimeFilterLabel(timeWindow)}
                value={timeWindow}
                options={TIME_FILTERS}
                onValueChange={handleTimeWindowChange}
              />
              <DropdownFilterPill
                label={getEntryFilterLabel(entryTypeFilter)}
                value={entryTypeFilter}
                options={ENTRY_TYPE_FILTERS}
                onValueChange={handleEntryTypeChange}
              />
              <SceneFilterPill
                groups={sceneOptions}
                value={sceneTagFilter}
                onValueChange={handleSceneTagChange}
              />
              {feedTab === 'nearby' ? (
                <DropdownFilterPill
                  label={getDistanceFilterLabel(radiusMeters)}
                  value={String(radiusMeters)}
                  options={DISTANCE_FILTERS.map((item) => ({
                    label: item.label,
                    value: String(item.radiusMeters),
                  }))}
                  onValueChange={(value) => handleRadiusChange(Number(value) as FeedDistanceFilter)}
                />
              ) : null}
            </View>
          </View>
        </View>

        {/* Sign-in notice */}
        {actionNotice ? (
          <View
            className="mx-auto w-full max-w-[1300px] pt-4"
            style={{ paddingHorizontal: isCompactWeb ? 22 : 24 }}>
            <View className="flex-row items-center justify-between gap-4 rounded-xl border border-border bg-surface px-4 py-3">
              <Text className="flex-1 text-sm font-medium text-foreground">{actionNotice}</Text>
              <Button size="sm" className="self-start" onPress={() => router.push('/sign-in')}>
                <Text>Sign in</Text>
              </Button>
            </View>
          </View>
        ) : null}

        {/* Feed + quiet desktop rail */}
        <View
          className="mx-auto w-full max-w-[1300px] pt-6"
          style={{
            flexDirection: isRailVisible ? 'row' : 'column',
            gap: isRailVisible ? 40 : 24,
            paddingHorizontal: isCompactWeb ? 22 : 24,
          }}>
          <View
            className="min-w-0 flex-1 gap-3"
            style={{ maxWidth: isRailVisible ? 760 : undefined } as any}>
            <Text className="text-xs text-muted-foreground">
              {nearbyFeedQuery.isFetching && visibleFeedItems.length > 0
                ? 'Refreshing...'
                : getFeedCountLabel(
                    visibleFeedEvents.length,
                    feedTab,
                    radiusMeters,
                    discoverFallback
                  )}
            </Text>

            <FeedContent
              items={visibleFeedItems}
              sourceTimeWindow={timeWindow}
              effectiveTimeWindow={effectiveTimeWindow}
              forceDateHeaders={feedTab === 'discover'}
              isLoading={nearbyFeedQuery.isPending}
              isError={nearbyFeedQuery.isError}
              onDealDirections={handleDealDirections}
              onDealSave={handleDealSave}
              onDealShare={handleDealShare}
              onEventRsvp={handleEventRsvp}
              onEventToggleSaved={handleEventToggleSaved}
              onRetry={() => nearbyFeedQuery.refetch()}
              compact={isCompactWeb}
            />
          </View>

          {isRailVisible ? (
            <WebFeedRail
              hotItems={railData.hotEvents}
              sceneItems={railData.scenes}
              venueCount={railData.venueCount}
            />
          ) : null}
        </View>
      </ScrollView>
      {rsvpDialogEvent ? (
        <WebFeedActionModal onClose={closeRsvpDialog}>
          {rsvpDialogEvent.viewer_rsvp_status === 'going' ? (
            <ManageRsvpSheetContent
              title={rsvpDialogEvent.title}
              venueName={rsvpDialogEvent.venue_name}
              startsAt={rsvpDialogEvent.starts_at}
              visibility={rsvpVisibility}
              isBusy={eventMutations.removeRsvp.isPending || eventMutations.confirmGoing.isPending}
              onChangeVisibility={handleRsvpVisibilityChange}
              onSaveVisibility={() => {
                handleConfirmRsvpDialog().catch(() => undefined);
              }}
              onCancelRsvp={handleCancelRsvpDialog}
              onClose={closeRsvpDialog}
            />
          ) : (
            <RsvpConfirmSheet
              title={rsvpDialogEvent.title}
              posterURL={rsvpDialogEvent.poster_url}
              posterImage={getCoverEventPosterImage(rsvpDialogEvent)}
              dayLabel={formatEventDayLabel(rsvpDialogEvent.starts_at)}
              distanceLabel={formatWebFeedDistanceLabel(rsvpDialogEvent.distance_meters)}
              timeLabel={formatEventTimeRange(rsvpDialogEvent.starts_at, rsvpDialogEvent.ends_at)}
              timeDetail={formatEventDayLabel(rsvpDialogEvent.starts_at)}
              venueName={rsvpDialogEvent.venue_name}
              venueDetail={(rsvpDialogEvent as any).address || null}
              visibility={rsvpVisibility}
              isBusy={eventMutations.confirmGoing.isPending}
              isFull={Boolean((rsvpDialogEvent as any).is_rsvp_full && rsvpDialogEvent.viewer_rsvp_status !== 'going')}
              onChangeVisibility={handleRsvpVisibilityChange}
              onConfirm={handleConfirmRsvpDialog}
              onClose={closeRsvpDialog}
            />
          )}
        </WebFeedActionModal>
      ) : null}
    </View>
  );
}

function WebFeedActionModal({
  children,
  onClose,
}: {
  children: React.ReactNode;
  onClose: () => void;
}) {
  return (
    <View className="absolute inset-0 z-[70] items-center justify-center bg-black/65 px-4">
      <Pressable
        accessibilityLabel="Close RSVP confirmation"
        className="absolute inset-0"
        onPress={onClose}
      />
      <View
        className="w-full max-w-[430px] rounded-[18px] border border-border bg-background p-4"
        style={{ maxHeight: 'calc(100vh - 32px)', overflowY: 'auto' } as any}>
        {children}
      </View>
    </View>
  );
}

function ModeTab({
  active,
  label,
  onPress,
}: {
  active: boolean;
  label: string;
  onPress: () => void;
}) {
  return (
    <Pressable
      className={cn(
        'rounded-[8px] px-3.5 py-1.5 active:opacity-80',
        active ? 'bg-primary' : 'bg-transparent'
      )}
      onPress={onPress}>
      <Text
        className={cn(
          'text-sm font-semibold',
          active ? 'text-primary-foreground' : 'text-muted-foreground'
        )}>
        {label}
      </Text>
    </Pressable>
  );
}

function DropdownFilterPill<TValue extends string>({
  label,
  onValueChange,
  options,
  value,
}: {
  label: string;
  onValueChange: (value: TValue) => void;
  options: { label: string; value: TValue }[];
  value: TValue;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Pressable className="flex-row items-center gap-1.5 rounded-full border border-border bg-surface px-3.5 py-[5px] active:opacity-80">
          <Text className="text-xs font-medium text-foreground">{label}</Text>
          <Icon as={ChevronDownIcon} size={12} className="text-muted-foreground" />
        </Pressable>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" sideOffset={8} className="w-44">
        <DropdownMenuRadioGroup value={value} onValueChange={(next) => onValueChange(next as TValue)}>
          {options.map((option) => (
            <DropdownMenuRadioItem key={option.value} value={option.value}>
              <Text>{option.label}</Text>
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function SceneFilterPill({
  groups,
  onValueChange,
  value,
}: {
  groups: EventTagGroup[];
  onValueChange: (value: string) => void;
  value: string;
}) {
  const selectedLabel = value === 'all' ? 'Scene' : getEventTagLabel(value);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Pressable className="flex-row items-center gap-1.5 rounded-full border border-border bg-surface px-3.5 py-[5px] active:opacity-80">
          <Text className="text-xs font-medium text-foreground">{selectedLabel}</Text>
          <Icon as={ChevronDownIcon} size={12} className="text-muted-foreground" />
        </Pressable>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" sideOffset={8} className="w-64">
        <DropdownMenuRadioGroup value={value} onValueChange={onValueChange}>
          <DropdownMenuRadioItem value="all">
            <Text>All scenes</Text>
          </DropdownMenuRadioItem>
          {groups.map((group) => (
            <React.Fragment key={group.key}>
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-[10px] uppercase tracking-[0.08em] text-muted-foreground">
                {group.label}
              </DropdownMenuLabel>
              {group.tags.map((tag) => (
                <DropdownMenuRadioItem key={tag.value} value={tag.value}>
                  <Text>{tag.label}</Text>
                </DropdownMenuRadioItem>
              ))}
            </React.Fragment>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function FeedContent({
  compact,
  effectiveTimeWindow,
  forceDateHeaders,
  items,
  isLoading,
  isError,
  onDealDirections,
  onDealSave,
  onDealShare,
  onEventRsvp,
  onEventToggleSaved,
  onRetry,
  sourceTimeWindow,
}: {
  compact: boolean;
  effectiveTimeWindow: FeedTimeWindow;
  forceDateHeaders: boolean;
  items: FeedItem[];
  isLoading: boolean;
  isError: boolean;
  onDealDirections: (item: FeedDealItem) => void;
  onDealSave: (item: FeedDealItem) => void;
  onDealShare: (item: FeedDealItem) => void;
  onEventRsvp: (event: FeedEvent) => void;
  onEventToggleSaved: (event: FeedEvent) => void;
  onRetry: () => void;
  sourceTimeWindow: FeedTimeWindow;
}) {
  const feedGridStyle = React.useMemo(
    () =>
      ({
        display: 'grid',
        gridTemplateColumns: compact ? 'minmax(0, 1fr)' : 'repeat(2, minmax(0, 1fr))',
        gridAutoFlow: 'dense',
        gap: compact ? 14 : 12,
        alignItems: 'start',
      }) as any,
    [compact]
  );
  const rows = React.useMemo(
    () =>
      buildDateGroupedFeedRows({
        effectiveTimeWindow,
        forceDateHeaders,
        items,
        sourceTimeWindow,
      }),
    [effectiveTimeWindow, forceDateHeaders, items, sourceTimeWindow]
  );

  if (isLoading) {
    return (
      <View style={feedGridStyle}>
        {[0, 1, 2, 3].map((item) => (
          <FeedSkeletonCard key={item} />
        ))}
      </View>
    );
  }

  if (isError) {
    return (
      <FeedStateCard
        icon={SearchIcon}
        title="Couldn't load this feed."
        body="Give it another try. If it keeps failing, the app will keep you moving."
        actionLabel="Try again"
        onAction={onRetry}
      />
    );
  }

  if (items.length === 0) {
    return (
      <FeedStateCard
        icon={CalendarDaysIcon}
        title="Nothing matched this view yet."
        body="Try a wider radius, a later time window, or clear the category filters."
        actionLabel="Refresh"
        onAction={onRetry}
      />
    );
  }

  return (
    <View style={feedGridStyle}>
      {rows.map((row) => {
        if (row.kind === 'date_header') {
          return <WebDateGroupHeader key={row.id} label={row.label} showDivider={!row.isFirst} />;
        }

        const item = row.item;
        const index = row.position - 1;
        if (item.type === FeedItemTypeDeal && item.deal) {
          if (item.deal.timing_state === DealTimingStateArchived) return null;
          return (
            <WebFeedDealCard
              key={item.deal.deal.id}
              compact={compact}
              fallbackIndex={index}
              item={item.deal}
              onDirections={onDealDirections}
              onSave={onDealSave}
              onShare={onDealShare}
            />
          );
        }
        if (item.event) {
          if (shouldRenderWebDealCard(item.event)) {
            return (
              <WebDealStrip
                key={item.event.id}
                compact={compact}
                event={item.event}
                onRsvp={onEventRsvp}
              />
            );
          }
          return (
            <WebEventCard
              key={item.event.id}
              event={item.event}
              fallbackIndex={index}
              onRsvp={onEventRsvp}
              onToggleSaved={onEventToggleSaved}
            />
          );
        }
        return null;
      })}
    </View>
  );
}

function WebDateGroupHeader({ label, showDivider }: { label: string; showDivider: boolean }) {
  const visibleLabel = showDivider ? label : `Next events · ${label}`;
  return (
    <View className="px-1 pb-1.5 pt-3" style={{ gridColumn: '1 / -1' } as any}>
      {showDivider ? <View className="mb-3 h-px bg-border/70" /> : null}
      <Text className="text-[11px] font-medium uppercase tracking-[0.08em] text-muted-foreground">
        {visibleLabel}
      </Text>
    </View>
  );
}

 





function FeedStateCard({
  icon,
  title,
  body,
  actionLabel,
  onAction,
}: {
  icon: React.ComponentProps<typeof Icon>['as'];
  title: string;
  body: string;
  actionLabel: string;
  onAction: () => void;
}) {
  return (
    <View
      className="items-start gap-4 rounded-2xl border border-border/60 bg-surface px-6 py-7"
      style={{ gridColumn: '1 / -1' } as any}>
      <View className="size-11 items-center justify-center rounded-2xl bg-primary/10">
        <Icon as={icon} size={20} className="text-primary" />
      </View>
      <View className="gap-2">
        <Text className="text-xl font-bold tracking-tight text-foreground">{title}</Text>
        <Text className="max-w-[480px] leading-6 text-muted-foreground">{body}</Text>
      </View>
      <Button variant="outline" className="rounded-xl" onPress={onAction}>
        <Text>{actionLabel}</Text>
      </Button>
    </View>
  );
}

function FeedSkeletonCard() {
  return (
    <View
      className="relative overflow-hidden rounded-[28px] bg-card"
      style={
        {
          aspectRatio: 4 / 5,
          boxShadow: '0 10px 24px rgba(0,0,0,0.55)',
        } as any
      }>
      <View className="absolute inset-0 bg-surface-raised" />
      <View
        className="pointer-events-none absolute bottom-0 left-0 right-0"
        style={
          {
            height: '55%',
            background:
              'linear-gradient(to bottom, rgba(4,4,8,0) 0%, rgba(4,4,8,0.88) 72%, rgba(4,4,8,1) 100%)',
          } as any
        }
      />
      <View className="absolute bottom-0 left-0 right-0 gap-2 px-5 pb-[22px]">
        <View className="rounded-md bg-white/10" style={{ height: 22, width: '75%' }} />
        <View className="rounded-md bg-white/10" style={{ height: 13, width: '52%' }} />
        <View className="mt-1 flex-row gap-1.5">
          <View className="h-6 w-16 rounded-full bg-white/10" />
          <View className="h-6 w-12 rounded-full bg-white/10" />
        </View>
      </View>
    </View>
  );
}

function filterWebFeedItemsByControls(
  items: FeedItem[],
  entryTypeFilter: EntryTypeFilter,
  sceneTagFilter: string
) {
  const hasEventOnlyFilter = entryTypeFilter !== 'all' || sceneTagFilter !== 'all';

  return items.filter((item) => {
    if (item.type === FeedItemTypeDeal) return !hasEventOnlyFilter;
    if (!item.event) return false;

    if (entryTypeFilter !== 'all' && !eventMatchesEntryFilter(item.event, entryTypeFilter)) {
      return false;
    }
    if (sceneTagFilter !== 'all' && !item.event.tags.includes(sceneTagFilter)) {
      return false;
    }
    return true;
  });
}

function eventMatchesEntryFilter(event: FeedEvent, entryTypeFilter: EntryTypeFilter) {
  if (entryTypeFilter === EntryTypeRSVP) {
    return event.entry_type === EntryTypeRSVP || event.entry_type === EntryTypeFreeRSVP;
  }
  return event.entry_type === entryTypeFilter;
}

function buildSceneFilterGroups(
  events: FeedEvent[],
  catalogGroups?: { category_group: string; label: string; tags: { label: string; slug: string }[] }[]
): EventTagGroup[] {
  const activeTags = new Set(
    events.flatMap((event) =>
      event.tags.flatMap((tag) => {
        const trimmed = tag.trim();
        return trimmed ? [trimmed] : [];
      })
    )
  );

	  if (catalogGroups?.length) {
	    return catalogGroups.flatMap((group) => {
	      const tags = group.tags.flatMap((tag) =>
	        activeTags.has(tag.slug)
	          ? [{
	          value: tag.slug,
	          label: tag.label,
	          colors: ['#ef4e2e'],
	        }]
	          : []
	      );

      return tags.length > 0
        ? [{
        key: group.category_group,
        label: group.label,
        tags,
      }]
        : [];
    });
  }

  const tags = Array.from(activeTags)
    .filter(isRailSceneTag)
    .sort((left, right) => getEventTagLabel(left).localeCompare(getEventTagLabel(right)))
    .map((tag) => ({
      value: tag,
      label: getEventTagLabel(tag),
      colors: ['#ef4e2e'],
    }));

  return tags.length > 0 ? [{ key: 'active-feed', label: 'In this feed', tags }] : [];
}

function buildWebFeedRailData(events: FeedEvent[]): {
  hotEvents: WebFeedRailEvent[];
  scenes: WebFeedRailScene[];
  venueCount: number;
} {
  const venueKeys = new Set<string>();
  const sceneMap = new Map<string, WebFeedRailScene>();

  events.forEach((event) => {
    const venueName = event.venue_name?.trim();
    if (venueName) {
      venueKeys.add(event.venue_id ?? venueName.toLowerCase());
    }

    event.tags.forEach((tag) => {
      const normalized = tag.trim();
      if (!isRailSceneTag(normalized)) return;
      const current = sceneMap.get(normalized) ?? {
        tag: normalized,
        label: getEventTagLabel(normalized),
        count: 0,
      };
      current.count += 1;
      sceneMap.set(normalized, current);
    });
  });

  const hotEvents = events
    .map((event) => ({
      event,
      label: getRailEventLabel(event),
      score: getEventTrendingScore(event),
    }))
    .sort((left, right) => right.score - left.score)
    .slice(0, 3);
  const scenes = Array.from(sceneMap.values())
    .sort((left, right) => right.count - left.count || left.label.localeCompare(right.label))
    .slice(0, 8);

  return {
    hotEvents,
    scenes,
    venueCount: venueKeys.size,
  };
}

function getRailEventLabel(event: FeedEvent) {
  if (event.is_boosted || event.promotion_label) return 'Featured';
  if (event.event_momentum === EventMomentumTrending) return 'Trending';
  if (event.event_momentum === EventMomentumHot) return 'Heating up';
  if (event.friends_going_count > 0) return 'Friends going';
  if (event.going_count > 0) return 'Crowd forming';
  if (isWebFeedEventHappeningNow(event)) return 'Happening now';
  return 'Upcoming';
}

function isRailSceneTag(tag: string) {
  return tag.length > 0 && !['archived', 'recent', 'casual'].includes(tag);
}

function getEventTrendingScore(event: FeedEvent) {
  const momentum =
    event.event_momentum === EventMomentumTrending
      ? 100
      : event.event_momentum === EventMomentumHot
        ? 60
        : 0;
  return Math.max(event.popularity_score ?? 0, momentum, event.going_count, 1);
}

function getEntryFilterLabel(value: EntryTypeFilter) {
  if (value === EntryTypeFreeRSVP) return 'Free RSVP';
  return ENTRY_TYPE_FILTERS.find((filter) => filter.value === value)?.label ?? 'Entry';
}

function getFeedCountLabel(
  count: number,
  mode: WebFeedMode,
  radiusMeters: FeedDistanceFilter,
  discoverFallback?: WebDiscoverFallback | null
) {
  if (mode === 'nearby') {
    return `${count} event${count === 1 ? '' : 's'} within ${getDistanceFilterLabel(radiusMeters)}`;
  }
  if (discoverFallback) {
    return `${count} result${count === 1 ? '' : 's'}`;
  }
  return `${count} result${count === 1 ? '' : 's'}`;
}

function getNextWebDiscoverFallback({
  currentFallback,
  entryTypeFilter,
  sceneTagFilter,
  timeWindow,
}: {
  currentFallback: WebDiscoverFallback | null;
  entryTypeFilter: EntryTypeFilter;
  sceneTagFilter: string;
  timeWindow: FeedTimeWindow;
}): WebDiscoverFallback | null {
  const hasAdvancedFilters = hasWebDiscoverAdvancedFilters(entryTypeFilter, sceneTagFilter);

  if (currentFallback) {
    if (currentFallback.stage === 'broaden' && hasAdvancedFilters) {
      return {
        entryTypeFilter: 'all',
        sceneTagFilter: 'all',
        stage: 'relax',
        timeWindow: currentFallback.timeWindow,
      };
    }

    const nextTimeWindow = getNextWebDiscoverFallbackWindow(currentFallback.timeWindow);
    if (!nextTimeWindow) {
      return null;
    }

    return {
      entryTypeFilter: currentFallback.entryTypeFilter,
      sceneTagFilter: currentFallback.sceneTagFilter,
      stage: currentFallback.stage,
      timeWindow: nextTimeWindow,
    };
  }

  const nextTimeWindow = getNextWebDiscoverFallbackWindow(timeWindow);
  if (nextTimeWindow) {
    return {
      entryTypeFilter,
      sceneTagFilter,
      stage: 'broaden',
      timeWindow: nextTimeWindow,
    };
  }

  if (!hasAdvancedFilters) {
    return null;
  }

  return {
    entryTypeFilter: 'all',
    sceneTagFilter: 'all',
    stage: 'relax',
    timeWindow,
  };
}

function getNextWebDiscoverFallbackWindow(timeWindow: FeedTimeWindow) {
  if (timeWindow === 'week') {
    return 'all';
  }
  if (timeWindow === 'all') {
    return null;
  }
  return WEB_DISCOVER_FALLBACK_WINDOWS[0];
}

function hasWebDiscoverAdvancedFilters(entryTypeFilter: EntryTypeFilter, sceneTagFilter: string) {
  return entryTypeFilter !== 'all' || sceneTagFilter !== 'all';
}

function shouldRenderWebDealCard(event: FeedEvent) {
  if (eventHasPoster(event)) return false;
  return Boolean(event.promotion_label?.trim()) || Boolean(event.editorial_label?.trim());
}

function eventHasPoster(event: FeedEvent) {
  if (event.poster_url?.trim()) return true;
  return (event.poster_images ?? []).some((image) => image.public_url?.trim());
}
